import type { FC, ReactNode } from 'react';
import { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  Stack,
  Tooltip,
  ButtonBase,
  Popover,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RouterLink } from 'src/components/router-link';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import { paths } from 'src/paths';
import type { NavColor } from 'src/types/settings';
import type { Section } from '../config';

export const SIDE_NAV_WIDTH: number = 100;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return useMemo(
    (): Record<string, string> => {
      if (isDarkMode) {
        return {
          '--nav-bg': 'transparent',
          '--nav-color': 'rgba(255, 255, 255, 0.95)',
          '--nav-border-color': 'transparent',
          '--nav-logo-border': 'rgba(255, 255, 255, 0.12)',
          '--nav-item-color': 'rgba(255, 255, 255, 0.7)',
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.06)',
          '--nav-item-active-bg': 'rgba(41, 112, 255, 0.15)',
          '--nav-item-active-color': '#ffffff',
          '--nav-item-icon-color': 'rgba(255, 255, 255, 0.5)',
          '--nav-item-icon-active-color': '#2970FF',
          '--nav-item-icon-inactive-color': '#ffffff',
          '--nav-scrollbar-color': 'rgba(255, 255, 255, 0.08)',
          '--nav-accent': '#2970FF',
          '--nav-popover-bg': '#111111',
          '--nav-popover-border': 'rgba(255, 255, 255, 0.1)',
          '--nav-popover-hover': 'rgba(255, 255, 255, 0.05)',
          '--nav-popover-text': 'rgba(255, 255, 255, 0.8)',
          '--nav-popover-icon': 'rgba(255, 255, 255, 0.5)',
          '--nav-hover-border': 'rgba(255, 255, 255, 0.1)',
        };
      }
      // Light mode
      return {
        '--nav-bg': 'transparent',
        '--nav-color': 'rgba(0, 0, 0, 0.87)',
        '--nav-border-color': 'transparent',
        '--nav-logo-border': 'rgba(0, 0, 0, 0.12)',
        '--nav-item-color': 'rgba(0, 0, 0, 0.6)',
        '--nav-item-hover-bg': 'rgba(0, 0, 0, 0.04)',
        '--nav-item-active-bg': 'rgba(41, 112, 255, 0.12)',
        '--nav-item-active-color': '#1e293b',
        '--nav-item-icon-color': 'rgba(0, 0, 0, 0.5)',
        '--nav-item-icon-active-color': '#2970FF',
        '--nav-item-icon-inactive-color': 'rgba(0, 0, 0, 0.7)',
        '--nav-scrollbar-color': 'rgba(0, 0, 0, 0.08)',
        '--nav-accent': '#2970FF',
        '--nav-popover-bg': '#f8fafc',
        '--nav-popover-border': 'rgba(0, 0, 0, 0.1)',
        '--nav-popover-hover': 'rgba(0, 0, 0, 0.04)',
        '--nav-popover-text': 'rgba(0, 0, 0, 0.7)',
        '--nav-popover-icon': 'rgba(0, 0, 0, 0.5)',
        '--nav-hover-border': 'rgba(0, 0, 0, 0.1)',
      };
    },
    [theme, color, isDarkMode]
  );
};

interface NavIconButtonProps {
  icon: ReactNode;
  title: string;
  active?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  hasPopover?: boolean;
}

const NavIconButton: FC<NavIconButtonProps> = ({
  icon,
  title,
  active,
  onClick,
  href,
  hasPopover
}) => {
  const buttonProps = href && !hasPopover
    ? {
        component: RouterLink,
        href: href
      }
    : {};

  return (
    <Tooltip title={active ? '' : title} placement="right">
      <ButtonBase
        onClick={onClick}
        sx={{
          alignItems: 'center',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: active ? 72 : 52,
          height: active ? 72 : 52,
          gap: 0.5,
          transition: 'all 0.2s ease',
          border: '1px solid transparent',
          ...(active && {
            background: 'var(--nav-item-active-bg)',
            borderColor: 'rgba(41, 112, 255, 0.3)',
            boxShadow: '0 4px 12px rgba(41, 112, 255, 0.15)',
          }),
          '&:hover': {
            backgroundColor: 'var(--nav-item-hover-bg)',
            borderColor: 'var(--nav-hover-border)',
          }
        }}
        {...buttonProps}
      >
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            color: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-inactive-color)',
            display: 'inline-flex',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            '& svg': {
              fontSize: active ? 22 : 24,
            }
          }}
        >
          {icon}
        </Box>
        {active && (
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 600,
              color: 'var(--nav-item-icon-active-color)',
              textAlign: 'center',
              lineHeight: 1.2,
              maxWidth: 64,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        )}
      </ButtonBase>
    </Tooltip>
  );
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { color = 'evident', sections = [] } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const [activeSection, setActiveSection] = useState<Section | null>(null);

  const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, section: Section) => {
    setPopoverAnchor(event.currentTarget);
    setActiveSection(section);
  }, []);

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchor(null);
    setActiveSection(null);
  }, []);

  const isPopoverOpen = Boolean(popoverAnchor);

  const isSectionActive = useCallback((section: Section): boolean => {
    if (section.singleLink && section.path) {
      return pathname === section.path;
    }
    return section.items.some(item => {
      if (item.path && pathname.startsWith(item.path)) {
        return true;
      }
      return false;
    });
  }, [pathname]);

  return (
    <>
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            ...cssVars,
            background: 'var(--nav-bg)',
            borderRight: 'none',
            color: 'var(--nav-color)',
            width: SIDE_NAV_WIDTH,
            boxShadow: 'none',
            overflowX: 'hidden',
            top: 64,
            height: 'calc(100% - 64px)',
            zIndex: 2,
          }
        }}
        variant="permanent"
      >
        <Scrollbar
          sx={{
            height: '100%',
            '& .simplebar-content': {
              height: '100%'
            },
            '& .simplebar-scrollbar:before': {
              background: 'var(--nav-scrollbar-color)',
              borderRadius: '4px',
              width: '4px',
            }
          }}
        >
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              py: 2,
              px: 1.5,
            }}
          >
            {/* Navigation Icons */}
            <Stack
              component="nav"
              spacing={1}
              sx={{
                flexGrow: 1,
                alignItems: 'center',
              }}
            >
              {sections.map((section, index) => {
                const isActive = isSectionActive(section);

                if (section.singleLink && section.path) {
                  return (
                    <NavIconButton
                      key={index}
                      icon={section.icon}
                      title={section.subheader || ''}
                      active={isActive}
                      href={section.path}
                    />
                  );
                }

                return (
                  <NavIconButton
                    key={index}
                    icon={section.icon}
                    title={section.subheader || ''}
                    active={isActive || (isPopoverOpen && activeSection === section)}
                    onClick={(e) => handlePopoverOpen(e, section)}
                    hasPopover
                  />
                );
              })}
            </Stack>



          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Popover Menu */}
      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            ...cssVars,
            background: 'var(--nav-popover-bg)',
            border: '1px solid var(--nav-popover-border)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            minWidth: 180,
            ml: 1,
            py: 0.75,
          }
        }}
      >
        {activeSection && (
          <>
            {activeSection.items.map((item, itemIndex) => {
              const isItemActive = item.path ? pathname.startsWith(item.path) : false;

              const linkProps = item.path
                ? item.external
                  ? {
                      component: 'a' as const,
                      href: item.path,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : {
                      component: RouterLink,
                      href: item.path,
                    }
                : {};

              return (
                <ListItemButton
                  key={itemIndex}
                  onClick={handlePopoverClose}
                  sx={{
                    py: 1,
                    px: 2,
                    minHeight: 40,
                    transition: 'background 0.15s ease',
                    ...(isItemActive && {
                      background: 'rgba(41, 112, 255, 0.12)',
                    }),
                    '&:hover': {
                      background: isItemActive
                        ? 'rgba(41, 112, 255, 0.18)'
                        : 'var(--nav-popover-hover)',
                    },
                  }}
                  {...linkProps}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      color: isItemActive ? 'var(--nav-item-icon-active-color)' : 'var(--nav-popover-icon)',
                      '& svg': {
                        fontSize: 18,
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      sx: {
                        color: isItemActive ? 'var(--nav-item-active-color)' : 'var(--nav-popover-text)',
                        fontSize: '0.8125rem',
                        fontWeight: isItemActive ? 600 : 500,
                      }
                    }}
                  />
                </ListItemButton>
              );
            })}
          </>
        )}
      </Popover>
    </>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(['blend-in', 'discreet', 'evident']),
  sections: PropTypes.array,
};
